import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { FC, useEffect, useState } from "react";
import TrackedServiceHeaderbarForm from "@/components/trackedServiceHeaderBar/trackedServiceHeaderbarForm";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { api } from "@/helper/api";
import dayjs, { Dayjs } from "dayjs";
import { useSelector } from "react-redux";
import { RootState } from "@/components/state/store";
import { useModalDismiss } from "@/helper/customHooks/useModalDismiss";
import { KAER_COMPANY_SUBJECT_ID } from "@/helper/systemIds";

interface TrackedServiceHeaderbarFormModalProps {
  modalOpen: boolean;
  handleModalOpen: (open: boolean) => void;
  handleSetError: (error: any) => void;
  handleNotificationMessage: (message: any) => void;
  handleNotificationVariant: (variant: any) => void;
  handleNotification: (value: boolean) => void;
  usePersistedSubjectSearchValue?: boolean;
}
type CompanySubject = {
  companyId: string;
  isParent: boolean;
  is_active: boolean;
  label: string;
  value: string;
};
type FormValues = {
  company_subject: { value: string; label: string } | string | CompanySubject;
  actual_duration: number | string;
  tracked_service_template: string | any;
  description: string;
  info_ops: string;
  executed_by: string | number;
  executed_on: string | Date | Dayjs;
  on_site: boolean;
  shouldAddAnotherEntry: boolean;
  price: any;
  estimated_duration: any;
  category: any;
  tracked_service_type: any;
};

const TrackedServiceHeaderbarFormModal: FC<
  TrackedServiceHeaderbarFormModalProps
> = ({
  modalOpen,
  handleModalOpen,
  handleSetError,
  handleNotification,
  handleNotificationMessage,
  handleNotificationVariant,
  usePersistedSubjectSearchValue = false,
}) => {
  const { t } = useTranslation();
  const [trackedServiceTemplateOptions, setTrackedServiceTemplateOptions] =
    useState<any[]>([]);
  const [trackedServiceTemplates, setTrackedServiceTemplates] = useState<any[]>(
    [],
  );
  const [kaerEmployeesOptions, setKaerEmployeesOptions] = useState<any[]>([]);
  const [resetDropdownSignal, setResetDropdownSignal] = useState<any>(0); // eslint-disable-line @typescript-eslint/no-unused-vars

  const loginStateForExecutedBy = useSelector(
    (state: RootState) => state.login,
  );

  const persistedSubjectSearchValue = useSelector(
    (state: RootState) => state.subjectSearch,
  );

  const methods = useForm<FormValues>({
    defaultValues: {
      company_subject:
        usePersistedSubjectSearchValue &&
        persistedSubjectSearchValue &&
        Object.keys(persistedSubjectSearchValue).length !== 0 &&
        "companyId" in persistedSubjectSearchValue
          ? persistedSubjectSearchValue
          : "",
      actual_duration: "",
      description: "",
      tracked_service_template: "",
      info_ops: "",
      executed_by: loginStateForExecutedBy.subjectId,
      executed_on: dayjs.utc().startOf("day").toISOString(),
      on_site: false,
      shouldAddAnotherEntry: false,
      price: "",
      estimated_duration: "",
      category: "",
      tracked_service_type: "",
    },
    mode: "onTouched",
    shouldFocusError: false,
  });

  useEffect(() => {
    if (modalOpen) {
      getKaerEmployees(setKaerEmployeesOptions, handleSetError);
      getTrackedServiceTemplates(
        setTrackedServiceTemplateOptions,
        setTrackedServiceTemplates,
        handleSetError,
      );
    }
  }, [modalOpen]);

  const selectedTemplateId: any = useWatch({
    control: methods.control,
    name: "tracked_service_template",
  });

  useEffect(() => {
    if (selectedTemplateId) {
      const selectedTemplate = trackedServiceTemplates.find(
        (template: any) => template.id === selectedTemplateId?.value,
      );
      if (selectedTemplate) {
        methods.setValue("price", selectedTemplate?.price);
        methods.setValue(
          "estimated_duration",
          selectedTemplate?.estimated_duration,
        );
        methods.setValue("category", selectedTemplate?.category.id);
        methods.setValue(
          "tracked_service_type",
          selectedTemplate?.tracked_service_type.id,
        );
        methods.setValue("description", selectedTemplate?.description);
      }
    }
  }, [selectedTemplateId, methods]);

  const onSubmit = async (data: any) => {
    const result = {
      company_subject_set: [data.company_subject?.value],
      subject: data?.subject?.value,
      actual_duration: data.actual_duration,
      description: data.description,
      tracked_service_template: data.tracked_service_template?.value,
      info_ops: data.info_ops,
      executed_by: data.executed_by?.value,
      executed_on: data.executed_on,
      on_site: data.on_site === "yes" || data.on_site === true,
      price: data.price,
      estimated_duration: data.estimated_duration,
      category: data.category,
      tracked_service_type: data.tracked_service_type,
    };

    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };

    api.genericApiRequest({
      entity: "trackedService",
      method: "post",
      successHandler: () => {
        handleNotificationMessage(t("save_successful"));
        handleNotificationVariant("success");
        handleNotification(true);
        setTimeout(() => {
          handleNotification(false);
          handleNotificationMessage("");
          handleNotificationVariant(undefined);
        }, 200);
        if (data.shouldAddAnotherEntry) {
          methods.resetField("actual_duration", { defaultValue: 0 });

          //if templatefield should be resettet reactive this

          // methods.resetField("tracked_service_template", {
          //   defaultValue: null,
          // });
          // setResetDropdownSignal((prev: any) => prev + 1);

          methods.resetField("info_ops");
        } else {
          methods.reset();
          handleModalOpen(false);
        }
      },
      failHandler: (error: any) => {
        handleSetError(error);
      },
      submitData: result,
      config: config,
    });
  };
  const closeModal = () => {
    handleModalOpen(false);
    methods.reset();
  };

  const cancelClose = useModalDismiss(modalOpen, closeModal);

  return (
    <Dialog
      open={modalOpen}
      {...cancelClose}
      sx={{
        "& .MuiPaper-root": {
          width: "80vw",
        },
      }}
    >
      <DialogTitle>{t("trackedservice_create")}</DialogTitle>
      <DialogContent>
        <FormProvider {...methods}>
          <TrackedServiceHeaderbarForm
            onSubmit={onSubmit}
            trackedServiceTemplateOptions={trackedServiceTemplateOptions}
            kaerEmployeesOptions={kaerEmployeesOptions}
            resetSignal={resetDropdownSignal}
            persistedSubjectSearchValue={
              usePersistedSubjectSearchValue &&
              persistedSubjectSearchValue &&
              Object.keys(persistedSubjectSearchValue).length !== 0 &&
              "companyId" in persistedSubjectSearchValue
                ? persistedSubjectSearchValue
                : undefined
            }
          />
        </FormProvider>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <GenericButton variant="outlined" onClick={closeModal}>
          {t("cancel")}
        </GenericButton>
        <div style={{ display: "flex", gap: "0.25rem" }}>
          <GenericButton
            type="submit"
            form="trackedService_headerBar_form"
            onClick={() => methods.setValue("shouldAddAnotherEntry", false)}
          >
            {t("save")}
          </GenericButton>
          <GenericButton
            type="submit"
            form="trackedService_headerBar_form"
            onClick={() => methods.setValue("shouldAddAnotherEntry", true)}
          >
            {t("save_additional_entry")}
          </GenericButton>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default TrackedServiceHeaderbarFormModal;

function getTrackedServiceTemplates(
  setTrackedServiceTemplateOptions: any,
  setTrackedServiceTemplates: any,
  handleSetError: any,
) {
  api.genericApiRequest({
    entity: "trackedServiceTemplate",
    method: "get",
    getAllPages: true,
    successHandler: (res: any) => {
      const trackedServiceTemplateOptions = res.data.results.map(
        (item: any) => ({
          value: item.id,
          label: item.internal_name,
        }),
      );
      setTrackedServiceTemplateOptions(trackedServiceTemplateOptions);
      setTrackedServiceTemplates(res.data.results);
    },
    failHandler: (error: any) => {
      handleSetError(error);
    },
  });
}
function getKaerEmployees(setKaerEmployeesOptions: any, handleSetError: any) {
  const additionalUserParameters: any = {};
  additionalUserParameters["impersonate_subject"] = KAER_COMPANY_SUBJECT_ID;
  additionalUserParameters["is_active"] = "true";
  additionalUserParameters["include_relations"] = true;

  api.genericApiRequest({
    entity: "user",
    method: "get",
    getAllPages: true,
    parametersToRender: {
      additionalUrlParameters: additionalUserParameters,
      depth: "0",
    },

    successHandler: (res: any) => {
      const employeeOptions = res.data.results.map((item: any) => ({
        value: item.person.subject,
        label: item.last_name + ", " + item.first_name,
      }));

      setKaerEmployeesOptions(
        employeeOptions.sort((a: any, b: any) =>
          a.label.localeCompare(b.label),
        ),
      );
    },
    failHandler: (error: any) => {
      handleSetError(error);
    },
  });
}
